import * as React from 'react';

import { Global, css } from '@emotion/react';
import Slider, { SliderProps, SliderRef } from 'rc-slider';

import { styled } from '../theme';
import theme from '../theme';

const trackColor = theme.color.primary1;
const handleColor = theme.color.primary1;
const handleActiveColor = theme.color.primary3;

const globalStyles = css`
    .rc-slider {
        position: relative;
        height: 14px;
        padding: 5px 0;
        width: 100%;
        border-radius: 6px;
        -ms-touch-action: none;
        touch-action: none;
        box-sizing: border-box;
        -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    }

    .rc-slider * {
        box-sizing: border-box;
        -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    }

    .rc-slider-rail {
        position: absolute;
        width: 100%;
        background-color: #e9e9e9;
        height: 4px;
        border-radius: 6px;
    }

    .rc-slider-track {
        position: absolute;
        left: 0;
        height: 4px;
        border-radius: 6px;
        background-color: ${trackColor};
    }

    .rc-slider-handle {
        position: absolute;
        margin-left: -7px;
        margin-top: -5px;
        width: 14px;
        height: 14px;
        cursor: pointer;
        cursor: -webkit-grab;
        cursor: grab;
        border-radius: 50%;
        border: solid 2px ${handleColor};
        background-color: #fff;
        -ms-touch-action: pan-x;
        touch-action: pan-x;
    }

    .rc-slider-handle:focus {
        border-color: ${handleActiveColor};
        box-shadow: 0 0 0 5px ${handleColor};
        outline: none;
    }

    .rc-slider-handle-click-focused:focus {
        border-color: ${handleColor};
        box-shadow: unset;
    }

    .rc-slider-handle:hover {
        border-color: ${handleActiveColor};
    }

    .rc-slider-handle:active {
        border-color: ${handleActiveColor};
        box-shadow: 0 0 5px ${handleActiveColor};
        cursor: -webkit-grabbing;
        cursor: grabbing;
    }

    .rc-slider-mark {
        display: flex;
        justify-content: space-between;
        align-items: center;
        height: 35px;
    }

    .rc-slider-mark-text {
        display: inline-block;
        color: #999;
        font-size: 12px;
        transform: none !important;
    }

    .rc-slider-mark-text-active {
        color: #666;
    }

    .rc-slider-step {
        position: absolute;
        width: 100%;
        height: 4px;
        background: transparent;
    }

    .rc-slider-dot {
        position: absolute;
        bottom: -2px;
        margin-left: -4px;
        width: 8px;
        height: 8px;
        border: 2px solid #e9e9e9;
        background-color: #fff;
        cursor: pointer;
        border-radius: 50%;
        vertical-align: middle;
    }

    .rc-slider-dot-active {
        border-color: ${handleColor};
    }

    .rc-slider-disabled {
        background-color: #e9e9e9;
    }

    .rc-slider-disabled .rc-slider-track {
        background-color: #ccc;
    }

    .rc-slider-disabled .rc-slider-handle,
    .rc-slider-disabled .rc-slider-dot {
        border-color: #ccc;
        box-shadow: none;
        background-color: #fff;
        cursor: not-allowed;
    }

    .rc-slider-disabled .rc-slider-mark-text,
    .rc-slider-disabled .rc-slider-dot {
        cursor: not-allowed !important;
    }

    .rc-slider-vertical {
        width: 14px;
        height: 100%;
        padding: 0 5px;
    }

    .rc-slider-vertical .rc-slider-rail {
        height: 100%;
        width: 4px;
    }

    .rc-slider-vertical .rc-slider-track {
        left: 5px;
        bottom: 0;
        width: 4px;
    }

    .rc-slider-vertical .rc-slider-handle {
        margin-left: -5px;
        margin-bottom: -7px;
        -ms-touch-action: pan-y;
        touch-action: pan-y;
    }

    .rc-slider-vertical .rc-slider-mark {
        top: 0;
        left: 18px;
        height: 100%;
    }

    .rc-slider-vertical .rc-slider-step {
        height: 100%;
        width: 4px;
    }

    .rc-slider-vertical .rc-slider-dot {
        left: 2px;
        margin-bottom: -4px;
    }

    .rc-slider-vertical .rc-slider-dot:first-of-type {
        margin-bottom: -4px;
    }

    .rc-slider-vertical .rc-slider-dot:last-child {
        margin-bottom: -4px;
    }
`;

interface RangeSliderProps extends Omit<SliderProps, 'range'> {
    tipFormatter?: (value: number) => React.ReactNode;
    handleStyle?: React.CSSProperties | React.CSSProperties[];
}

const SliderTooltipContainer = styled('div')({
    position: 'absolute',
    bottom: '24px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    flexDirection: 'column',
    opacity: 0.9,
    maxWidth: 350,
    zIndex: theme.zIndex.popovers,
});

const SliderTooltipContent = styled('div')(({ theme }) => ({
    color: theme.color.surface,
    backgroundColor: 'rgba(0, 0, 0, 1)',
    borderRadius: theme.borderRadius.base,
    padding: '8px 12px',
    fontSize: '12px',
    lineHeight: '1.3em',
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
}));

const SliderTooltipArrow = styled('div')({
    position: 'absolute',
    top: '100%',
    left: '50%',
    marginLeft: '-6px',
    width: 0,
    height: 0,
    borderLeft: '6px solid transparent',
    borderRight: '6px solid transparent',
    borderTop: '6px solid rgba(0, 0, 0, 1)',
    pointerEvents: 'none',
});

const TooltipWrapper = ({ node, value, dragging, tipFormatter }) => {
    const [showTooltip, setShowTooltip] = React.useState(false);

    if (!tipFormatter) {
        return node;
    }

    const handleMouseEnter = React.useCallback(() => setShowTooltip(true), []);
    const handleMouseLeave = React.useCallback(() => setShowTooltip(false), []);

    return React.cloneElement(node, {
        ...node.props,
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
        children: [
            node.props.children,
            (showTooltip || dragging) && (
                <SliderTooltipContainer key="tooltip">
                    <SliderTooltipContent>{tipFormatter(value)}</SliderTooltipContent>
                    <SliderTooltipArrow />
                </SliderTooltipContainer>
            ),
        ].filter(Boolean),
    });
};

export default React.forwardRef<SliderRef, RangeSliderProps>((props, ref) => {
    const { tipFormatter, handleStyle, ...sliderProps } = props;

    let styles = sliderProps.styles;
    if (handleStyle && !styles) {
        styles = { handle: Array.isArray(handleStyle) ? handleStyle[0] : handleStyle };
    }

    const handleRender = tipFormatter
        ? (node, handleProps) => {
              return (
                  <TooltipWrapper
                      node={node}
                      value={handleProps.value}
                      dragging={handleProps.dragging}
                      tipFormatter={tipFormatter}
                  />
              );
          }
        : undefined;

    return (
        <>
            <Global styles={globalStyles} />
            <Slider range ref={ref} handleRender={handleRender} styles={styles} {...sliderProps} />
        </>
    );
});
